"use client";

import type { ComponentProps } from "react";

import { tryFormatDate, tryFormatDateTooltip } from "./formatDate";
import { useResolveI18n } from "./resolveI18n";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { sanitizeHtml } from "@/lib/html/htmlSanitizer";
import { take } from "@/lib/range";
import { cn } from "@/lib/utils/cn";

const HTML_TAG_RE = /<[a-z][\s\S]*?>/i;

export type SpecRow = {
  label: string;
  value: string;
  emphasis?: boolean;
};

function formatValue(raw: string): string {
  const trimmed = raw.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return raw;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
}

function isJsonValue(raw: string): boolean {
  const trimmed = raw.trim();
  return (trimmed.startsWith("{") || trimmed.startsWith("[")) && trimmed.length > 2;
}

export function SpecSheet({
  title,
  subtitle,
  rows,
  visibleCount,
  className,
  ...props
}: Omit<
  ComponentProps<"div">,
  "children" | "title" | "subtitle" | "rows" | "visibleCount"
> & {
  title?: string;
  subtitle?: string;
  rows: readonly SpecRow[];
  visibleCount: number;
}) {
  const resolve = useResolveI18n();
  return (
    <div
      data-slot="spec-sheet"
      className={cn(
        "flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-4",
        className,
      )}
      {...props}
    >
      {(title || subtitle) && (
        <div className="flex flex-col gap-0.5">
          {title && <span className="text-sm font-semibold text-foreground">{title}</span>}
          {subtitle && (
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          )}
        </div>
      )}

      <div className="flex flex-col">
        {take(rows, visibleCount).map((row, i) => (
          <div
            key={row.label}
            className={cn(
              "flex items-start gap-3 border-t border-border/50 py-2 animate-in fade-in fill-mode-both duration-300 first:border-t-0 first:pt-0",
            )}
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <span className="w-32 shrink-0 text-xs font-medium text-muted-foreground">
              {row.label}
            </span>
            {HTML_TAG_RE.test(row.value) ? (
              <div
                className={cn(
                  "spec-sheet-html min-w-0 flex-1 text-sm break-words",
                  row.emphasis
                    ? "font-medium text-foreground"
                    : "text-foreground/80",
                )}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(row.value) }}
              />
            ) : isJsonValue(row.value) ? (
              <pre
                className={cn(
                  "min-w-0 flex-1 overflow-x-auto rounded bg-muted/50 px-2 py-1 text-xs leading-relaxed",
                  row.emphasis
                    ? "font-medium text-foreground"
                    : "text-foreground/80",
                )}
              >
                <code>{formatValue(row.value)}</code>
              </pre>
            ) : (() => {
              const relative = tryFormatDate(row.value);
              const textClass = cn(
                "min-w-0 flex-1 text-sm whitespace-pre-wrap break-words",
                row.emphasis ? "font-medium text-foreground" : "text-foreground/80",
              );
              if (relative) {
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={cn(textClass, "cursor-default")}>{relative}</span>
                    </TooltipTrigger>
                    <TooltipContent>{tryFormatDateTooltip(row.value)}</TooltipContent>
                  </Tooltip>
                );
              }
              return <span className={textClass}>{resolve(row.value)}</span>;
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}
